import React from "react";
import { Navbar } from "../../Components/Navbar/Navbar";
import { Footer } from "../../Components/Footer/footer";
import "./admin-proiecte-comunicate.css";
import {
    addDoc, collection, getDocs, orderBy, query,
    serverTimestamp, Timestamp, updateDoc, doc, deleteDoc
} from "firebase/firestore";
import { db, storage } from "../../firebase";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";

type Form = { title: string; url: string; date: string; description: string; };
type PR = { id: string; title: string; url: string; imageUrl?: string; date?: Timestamp; description?: string; createdAt?: Timestamp; updatedAt?: Timestamp; };

export default function AdminProiecteComunicatePage() {
    const [form, setForm] = React.useState<Form>({ title: "", url: "", date: "", description: "" });
    const [file, setFile] = React.useState<File | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [progress, setProgress] = React.useState(0);
    const [items, setItems] = React.useState<PR[]>([]);
    const [msg, setMsg] = React.useState("");

    // NEW: track edit mode (id of the item being edited; null = create mode)
    const [editingId, setEditingId] = React.useState<string | null>(null);

    const load = React.useCallback(async () => {
        const q = query(collection(db, "project_press_releases"), orderBy("date", "desc"));
        const snap = await getDocs(q);
        setItems(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as PR[]);
    }, []);
    React.useEffect(() => { load(); }, [load]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setForm(p => ({ ...p, [name]: value }));
    };

    // NEW: populate form with the selected item for editing
    const startEdit = (it: PR) => {
        setEditingId(it.id);
        setForm({
            title: it.title ?? "",
            url: it.url ?? "",
            date: it.date?.toDate?.().toISOString().slice(0, 10) ?? "",
            description: it.description ?? "",
        });
        setFile(null);
        setProgress(0);
        setMsg("");
    };

    // NEW: cancel edit and return to create mode
    const cancelEdit = () => {
        setEditingId(null);
        setForm({ title: "", url: "", date: "", description: "" });
        setFile(null);
        setProgress(0);
        setMsg("");
    };

    // NEW: delete an item (Firestore doc only)
    const handleDelete = async (it: PR) => {
        if (!window.confirm(`Ștergi „${it.title}”?`)) return;
        try {
            await deleteDoc(doc(db, "project_press_releases", it.id));
            setMsg("✔️ Comunicat șters.");
            // if we were editing this one, exit edit mode
            if (editingId === it.id) cancelEdit();
            load();
        } catch (e) {
            console.error(e);
            setMsg("❌ Eroare la ștergere.");
        }
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.title || !form.url) { setMsg("Titlul și URL sunt obligatorii."); return; }
        try {
            setSaving(true); setMsg(""); setProgress(0);

            // EDIT FLOW
            if (editingId) {
                const existing = items.find(x => x.id === editingId);
                if (!existing) { setMsg("Elementul pentru editare nu mai există."); setSaving(false); return; }

                let imageUrl = existing.imageUrl ?? "";

                // If a new file is chosen, upload it and replace imageUrl
                if (file) {
                    const storagePath = `project_press/${Date.now()}-${file.name}`;
                    const storageRef = ref(storage, storagePath);
                    const task = uploadBytesResumable(storageRef, file);
                    await new Promise<void>((resolve, reject) => {
                        task.on("state_changed",
                            s => setProgress(Math.round((s.bytesTransferred / s.totalBytes) * 100)),
                            reject, resolve
                        );
                    });
                    imageUrl = await getDownloadURL(task.snapshot.ref);
                }

                await updateDoc(doc(db, "project_press_releases", editingId), {
                    title: form.title.trim(),
                    url: form.url.trim(),
                    imageUrl,
                    description: form.description.trim(),
                    date: form.date ? Timestamp.fromDate(new Date(form.date)) : (existing.date ?? serverTimestamp()),
                    updatedAt: serverTimestamp(),
                });

                setMsg("✔️ Comunicat actualizat.");
                cancelEdit();
                load();
                return;
            }

            // CREATE FLOW (your original behavior)
            let imageUrl: string | undefined;

            if (file) {
                const storagePath = `project_press/${Date.now()}-${file.name}`;
                const storageRef = ref(storage, storagePath);
                const task = uploadBytesResumable(storageRef, file);
                await new Promise<void>((resolve, reject) => {
                    task.on("state_changed",
                        s => setProgress(Math.round((s.bytesTransferred / s.totalBytes) * 100)),
                        reject, resolve
                    );
                });
                imageUrl = await getDownloadURL(task.snapshot.ref);
            }

            const payload = {
                title: form.title.trim(),
                url: form.url.trim(),
                imageUrl: imageUrl ?? "",
                description: form.description.trim(),
                date: form.date ? Timestamp.fromDate(new Date(form.date)) : serverTimestamp(),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };
            const created = await addDoc(collection(db, "project_press_releases"), payload as any);
            await updateDoc(doc(db, "project_press_releases", created.id), { updatedAt: serverTimestamp() });

            setMsg("✔️ Comunicat adăugat.");
            setForm({ title: "", url: "", date: "", description: "" });
            setFile(null); setProgress(0);
            load();
        } catch (e: any) {
            console.error(e); setMsg("❌ Eroare la salvare.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="page-container">
            <Navbar />
            <div className="page-content">
                <h1>Admin – Comunicate de presă (Proiecte)</h1>

                <form className="pr-form" onSubmit={onSubmit}>
                    <div className="grid">
                        <label> Titlu
                            <input name="title" value={form.title} onChange={onChange} placeholder="Ex: Comunicat proiect X" />
                        </label>
                        <label> URL
                            <input name="url" value={form.url} onChange={onChange} placeholder="https://..." />
                        </label>
                        <label> Data
                            <input type="date" name="date" value={form.date} onChange={onChange} />
                        </label>
                    </div>

                    <label> Descriere (opțional)
                        <textarea name="description" rows={3} value={form.description} onChange={onChange} />
                    </label>

                    <label> Imagine (opțional)
                        <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                        {file && <div className="preview"><img src={URL.createObjectURL(file)} alt="preview" /></div>}
                    </label>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button className="primary" type="submit" disabled={saving}>
                            {saving
                                ? editingId ? `Se actualizează… ${progress}%` : `Se salvează… ${progress}%`
                                : editingId ? "Actualizează comunicatul" : "Salvează comunicatul"}
                        </button>
                        {editingId && (
                            <button type="button" onClick={cancelEdit}>
                                Renunță editarea
                            </button>
                        )}
                    </div>

                    {msg && <p className="message">{msg}</p>}
                </form>

                <div className="pr-list">
                    <h2>Ultimele comunicate</h2>
                    {items.length === 0 ? <p>— niciunul —</p> : (
                        <ul>
                            {items.map(it => (
                                <li key={it.id}>
                                    {it.imageUrl && <img src={it.imageUrl} alt="" />}
                                    <div>
                                        <a href={it.url} target="_blank" rel="noreferrer">{it.title}</a>
                                        <div className="meta">
                                            {it.date?.toDate?.() ? it.date.toDate().toLocaleDateString() : ""} {it.description ? `• ${it.description}` : ""}
                                        </div>

                                        {/* NEW: actions */}
                                        <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                                            <button type="button" onClick={() => startEdit(it)}>Editează</button>
                                            <button type="button" onClick={() => handleDelete(it)}>Șterge</button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

            </div>
            <Footer />
        </div>
    );
}